import axios from 'axios';
import api from './api';

// Create dedicated axios instance for Field Engineer API
const fieldEngineerApi = axios.create({
  baseURL: process.env.REACT_APP_FIELD_ENGINEER_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for error handling
fieldEngineerApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Field Engineer API Error:', error);
    return Promise.reject(error);
  }
);

export const fieldEngineerService = {
  /**
   * Update the Field Engineer API token for a ticket (stored in dev.ssk backend)
   * @param {number} ticketId - The ticket ID
   * @param {string} token - The API token
   */
  updateToken: async (ticketId, token) => {
    try {
      const response = await api.put(
        `/ticket/updateFieldEngineerToken/${ticketId}`,
        { token }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating field engineer token:', error);
      throw error;
    }
  },

  /**
   * Get all field engineers from the external Field Engineer API
   * @param {string} token - The API token for authentication
   */
  getAllFieldEngineers: async (token) => {
    try {
      const response = await fieldEngineerApi.get('/field-engineers', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching field engineers:', error);
      throw error;
    }
  },

  /**
   * Get single field engineer by ID from the external Field Engineer API
   * @param {number} engineerId - The field engineer ID
   * @param {string} token - The API token for authentication
   */
  getFieldEngineerById: async (engineerId, token) => {
    try {
      const response = await fieldEngineerApi.get(`/field-engineers/${engineerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching field engineer:', error);
      throw error;
    }
  },

  /**
   * Get field engineer profile image from the authenticated endpoint
   * @param {string} imageFilename - The image filename (e.g., 'engineer-01-bob-marley.png')
   * @param {string} token - The API token for authentication
   * @returns {Promise<Blob>} - Image blob data
   */
  getEngineerImage: async (imageFilename, token) => {
    try {
      const response = await fieldEngineerApi.get(`/images/profile/${imageFilename}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob', // Important: tell axios to expect binary data
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching engineer image:', error);
      throw error;
    }
  },
};
